import React, { useEffect, useState } from 'react';
import { Checkbox, Input, InputNumber, Radio, Select, Space, Table, Tag } from 'antd';
import { Box, Check, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchUsersApi } from '../../../../api/admin/user.api';
import {
  AdminInventoryItem,
  CosmeticOwner,
  getCosmeticOwners,
  getGrantableItems,
  getUserCosmeticInventory,
  grantCosmetic,
  revokeCosmetic,
} from '../../../../api/admin/cosmetic.api';
import { StoreItem } from '../../../../api/payment/store.api';
import './cosmetic.css';
import { AdminButton, AdminCard, AdminPageHeader, AdminSectionTitle, AdminStatusPill } from '../../ui';

const RARITY_LABEL: Record<string, string> = {
  Common: 'Cổ điển',
  Rare: 'Hiếm',
  Epic: 'Sử thi',
  Legendary: 'Huyền thoại',
  Mythic: 'Thần thoại',
};
const RARITY_COLOR: Record<string, string> = {
  Common: 'default',
  Rare: 'blue',
  Epic: 'purple',
  Legendary: 'gold',
  Mythic: 'red',
};
const TYPE_LABEL: Record<string, string> = {
  Nametag: 'Khung tên',
  AvatarEffect: 'Khung avatar',
};

const equipPill = (e: boolean) =>
  e ? (
    <AdminStatusPill tone="success" icon={<Check size={12} />}>
      Đang gắn
    </AdminStatusPill>
  ) : (
    <AdminStatusPill tone="neutral" icon={<Box size={12} />}>
      Trong kho
    </AdminStatusPill>
  );

const rarityTag = (rarity?: string | null) =>
  rarity ? <Tag color={RARITY_COLOR[rarity] || 'default'}>{RARITY_LABEL[rarity] || rarity}</Tag> : null;

interface SelectedUser {
  userId: string;
  fullName?: string;
  email?: string;
  userName?: string;
}

const ManagementCosmetic = () => {
  // --- Tìm user ---
  const [keyword, setKeyword] = useState('');
  const [users, setUsers] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<SelectedUser | null>(null);

  // --- Kho cosmetic của user đã chọn ---
  const [inventory, setInventory] = useState<AdminInventoryItem[]>([]);
  const [loadingInv, setLoadingInv] = useState(false);

  // --- Danh sách TẤT CẢ user đang sở hữu cosmetic ---
  const [owners, setOwners] = useState<CosmeticOwner[]>([]);
  const [ownersTotal, setOwnersTotal] = useState(0);
  const [ownersPage, setOwnersPage] = useState(1);
  const [ownersKeyword, setOwnersKeyword] = useState('');
  const [loadingOwners, setLoadingOwners] = useState(false);
  const OWNERS_PAGE_SIZE = 20;

  const loadOwners = async (page = 1, kw = '') => {
    setLoadingOwners(true);
    try {
      const res = await getCosmeticOwners(page, OWNERS_PAGE_SIZE, kw);
      setOwners(res.items || []);
      setOwnersTotal(res.total || 0);
      setOwnersPage(res.pageNumber || page);
    } catch {
      toast.error('Không tải được danh sách khách sở hữu cosmetic');
    }
    setLoadingOwners(false);
  };

  useEffect(() => {
    loadOwners(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Form cấp ---
  const [grantType, setGrantType] = useState<'Nametag' | 'AvatarEffect'>('Nametag');
  const [items, setItems] = useState<StoreItem[]>([]);
  const [selectedCode, setSelectedCode] = useState<string | undefined>();
  const [durationMode, setDurationMode] = useState<'item' | 'permanent' | 'custom'>('item');
  const [customDays, setCustomDays] = useState<number>(365);
  const [equip, setEquip] = useState(true);
  const [granting, setGranting] = useState(false);

  const searchUsers = async () => {
    if (!keyword.trim()) return;
    setSearching(true);
    try {
      const filters = keyword.includes('@') ? { email: keyword.trim() } : { fullName: keyword.trim() };
      const res: any = await fetchUsersApi(1, 20, filters);
      setUsers(res?.data?.data || res?.data?.items || res?.data || []);
    } catch (e) {
      toast.error('Tìm người dùng lỗi');
    }
    setSearching(false);
  };

  const loadInventory = async (userId: string) => {
    setLoadingInv(true);
    try {
      setInventory(await getUserCosmeticInventory(userId));
    } catch {
      toast.error('Không tải được kho của user');
    }
    setLoadingInv(false);
  };

  useEffect(() => {
    getGrantableItems(grantType)
      .then(setItems)
      .catch(() => setItems([]));
    setSelectedCode(undefined);
  }, [grantType]);

  const onSelectUser = (u: any) => {
    const su: SelectedUser = { userId: u.userId, fullName: u.fullName, email: u.email, userName: u.userName };
    setSelected(su);
    loadInventory(su.userId);
  };

  const doGrant = async () => {
    if (!selected || !selectedCode) {
      toast.error('Hãy chọn user và vật phẩm');
      return;
    }
    // eslint-disable-next-line no-alert
    const totp = window.prompt('Nhập mã Authenticator (6 số) để xác nhận cấp cosmetic:')?.trim();
    if (!totp || totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số để cấp cosmetic.');
      return;
    }
    setGranting(true);
    try {
      const res = await grantCosmetic(
        {
          userId: selected.userId,
          code: selectedCode,
          permanent: durationMode === 'permanent',
          durationDays: durationMode === 'custom' ? customDays : null,
          equip,
        },
        totp
      );
      if (res.success) {
        toast.success(res.message || 'Đã cấp');
        loadInventory(selected.userId);
      } else {
        toast.error(res.message || 'Cấp thất bại');
      }
    } catch {
      toast.error('Cấp thất bại');
    }
    setGranting(false);
  };

  const doRevoke = async (code: string) => {
    if (!selected) return;
    // eslint-disable-next-line no-alert
    const totp = window.prompt('Nhập mã Authenticator (6 số) để xác nhận thu hồi:')?.trim();
    if (!totp || totp.length !== 6) {
      toast.error('Cần mã Authenticator 6 số để thu hồi.');
      return;
    }
    try {
      const res = await revokeCosmetic({ userId: selected.userId, code }, totp);
      if (res.success) {
        toast.success(res.message || 'Đã thu hồi');
        loadInventory(selected.userId);
      } else {
        toast.error(res.message || 'Thu hồi thất bại');
      }
    } catch {
      toast.error('Thu hồi thất bại');
    }
  };

  const userColumns = [
    { title: 'Tên', dataIndex: 'fullName', key: 'fullName' },
    { title: 'Email', dataIndex: 'email', key: 'email' },
    { title: 'Username', dataIndex: 'userName', key: 'userName' },
    {
      title: '',
      key: 'action',
      render: (_: any, u: any) => (
        <AdminButton variant="soft" size={32} onClick={() => onSelectUser(u)}>
          Chọn
        </AdminButton>
      ),
    },
  ];

  const invColumns = [
    { title: 'Vật phẩm', dataIndex: 'name', key: 'name' },
    {
      title: 'Loại',
      dataIndex: 'type',
      key: 'type',
      render: (t: string) => TYPE_LABEL[t] || t,
    },
    {
      title: 'Bậc',
      dataIndex: 'rarity',
      key: 'rarity',
      render: (r: string) => rarityTag(r),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'isEquipped',
      key: 'isEquipped',
      render: (e: boolean) => equipPill(e),
    },
    {
      title: 'Hạn dùng',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (d: string | null) => (d ? new Date(d).toLocaleDateString('vi-VN') : 'Vĩnh viễn'),
    },
    {
      title: '',
      key: 'action',
      render: (_: any, row: AdminInventoryItem) => (
        <AdminButton variant="danger-soft" size={32} onClick={() => doRevoke(row.code)}>
          Thu hồi
        </AdminButton>
      ),
    },
  ];

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Quản lý khung / hiệu ứng (cosmetic)"
        description="Tìm khách để xem và quản lý khung tên, khung avatar khách đang sở hữu."
        actions={
          <div className="adm-search-group">
            <Search size={16} className="adm-search-group__icon" />
            <input
              className="adm-search-group__input"
              placeholder="Tìm theo email hoặc tên"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchUsers()}
            />
            {keyword && (
              <button
                type="button"
                className="adm-search-group__clear"
                aria-label="Xoá"
                onClick={() => setKeyword('')}
              >
                <X size={14} />
              </button>
            )}
            <AdminButton size={36} loading={searching} onClick={searchUsers}>
              Tìm
            </AdminButton>
          </div>
        }
      />

      {/* Kết quả tìm user */}
      {users.length > 0 && (
        <AdminCard flush>
          <Table
            scroll={{ x: 'max-content' }}
            size="small"
            rowKey={(u) => u.userId}
            columns={userColumns}
            dataSource={users}
            pagination={false}
            loading={searching}
          />
        </AdminCard>
      )}

      {/* Danh sách TẤT CẢ khách đang sở hữu cosmetic */}
      <section className="adm-stack">
        <AdminSectionTitle
          title={`Khách đang sở hữu khung / hiệu ứng (${ownersTotal})`}
          extra={
            <div className="flex items-center gap-2">
              <Input
                className="adm-search"
                style={{ width: 320, maxWidth: '100%' }}
                prefix={<Search size={16} />}
                placeholder="Lọc theo email hoặc tên"
                value={ownersKeyword}
                onChange={(e) => setOwnersKeyword(e.target.value)}
                onPressEnter={() => loadOwners(1, ownersKeyword.trim())}
                allowClear
              />
              <AdminButton variant="soft" size={36} onClick={() => loadOwners(1, ownersKeyword.trim())}>
                Lọc
              </AdminButton>
            </div>
          }
        />
        <AdminCard flush>
        <Table
          scroll={{ x: 'max-content' }}
          size="small"
          rowKey={(r) => `${r.userId}-${r.itemCode}`}
          loading={loadingOwners}
          dataSource={owners}
          pagination={{
            current: ownersPage,
            pageSize: OWNERS_PAGE_SIZE,
            total: ownersTotal,
            showSizeChanger: false,
            onChange: (p) => loadOwners(p, ownersKeyword.trim()),
          }}
          columns={[
            {
              title: 'Khách',
              key: 'user',
              render: (_: any, r: CosmeticOwner) => (
                <div>
                  <div className="font-medium dark:text-white">
                    {r.fullName || r.userName || r.userId.slice(0, 8)}
                  </div>
                  <div className="text-xs text-[var(--adm-muted)]">{r.email || r.userName}</div>
                </div>
              ),
            },
            {
              title: 'Vật phẩm',
              key: 'item',
              render: (_: any, r: CosmeticOwner) => (
                <span>
                  {r.itemName} {rarityTag(r.rarity)}
                </span>
              ),
            },
            {
              title: 'Loại',
              dataIndex: 'itemType',
              key: 'itemType',
              render: (t: string) => TYPE_LABEL[t] || t,
            },
            {
              title: 'Trạng thái',
              dataIndex: 'isEquipped',
              key: 'isEquipped',
              render: (e: boolean) =>
                equipPill(e),
            },
            {
              title: 'Hạn dùng',
              dataIndex: 'expiresAt',
              key: 'expiresAt',
              render: (d: string | null) =>
                d ? new Date(d).toLocaleDateString('vi-VN') : 'Vĩnh viễn',
            },
            {
              title: '',
              key: 'action',
              render: (_: any, r: CosmeticOwner) => (
                <AdminButton
                  variant="soft"
                  size={32}
                  onClick={() =>
                    onSelectUser({
                      userId: r.userId,
                      fullName: r.fullName || undefined,
                      email: r.email || undefined,
                      userName: r.userName || undefined,
                    })
                  }
                >
                  Quản lý
                </AdminButton>
              ),
            },
          ]}
        />
        </AdminCard>
      </section>

      {/* User đã chọn */}
      {selected && (
        <AdminCard>
          <div className="mb-4 dark:text-white">
            Đang thao tác cho:{' '}
            <b>{selected.fullName || selected.userName || selected.email}</b>{' '}
            <span className="text-[var(--adm-muted)]">({selected.email || selected.userId})</span>
          </div>

          {/* Form cấp */}
          <div className="adm-panel mb-5">
            <div className="font-semibold mb-3 dark:text-white">Cấp cosmetic (miễn phí)</div>
            <Space wrap align="end" size="middle">
              <div>
                <div className="text-sm mb-1 dark:text-gray-300">Loại</div>
                <Select
                  value={grantType}
                  style={{ width: 150 }}
                  onChange={(v) => setGrantType(v)}
                  options={[
                    { value: 'Nametag', label: 'Khung tên' },
                    { value: 'AvatarEffect', label: 'Khung avatar' },
                  ]}
                />
              </div>
              <div>
                <div className="text-sm mb-1 dark:text-gray-300">Vật phẩm (bậc)</div>
                <Select
                  value={selectedCode}
                  placeholder="Chọn bậc"
                  style={{ width: 240 }}
                  onChange={(v) => setSelectedCode(v)}
                  options={items.map((i) => ({
                    value: i.code,
                    label: `${i.name}${i.rarity ? ` — ${RARITY_LABEL[i.rarity] || i.rarity}` : ''}`,
                  }))}
                />
              </div>
              <div>
                <div className="text-sm mb-1 dark:text-gray-300">Hạn dùng</div>
                <Radio.Group value={durationMode} onChange={(e) => setDurationMode(e.target.value)}>
                  <Radio value="item">Theo mặc định</Radio>
                  <Radio value="permanent">Vĩnh viễn</Radio>
                  <Radio value="custom">Tùy chỉnh</Radio>
                </Radio.Group>
                {durationMode === 'custom' && (
                  <InputNumber
                    className="ml-2"
                    min={1}
                    value={customDays}
                    onChange={(v) => setCustomDays(v || 1)}
                    addonAfter="ngày"
                  />
                )}
              </div>
              <Checkbox checked={equip} onChange={(e) => setEquip(e.target.checked)}>
                Gắn luôn
              </Checkbox>
              <AdminButton loading={granting} onClick={doGrant}>
                Cấp
              </AdminButton>
            </Space>
          </div>

          {/* Kho hiện có */}
          <div className="font-semibold mb-2 dark:text-white">Đang sở hữu</div>
          <Table
            scroll={{ x: 'max-content' }}
            size="small"
            rowKey={(r) => r.id}
            columns={invColumns}
            dataSource={inventory}
            loading={loadingInv}
            pagination={false}
          />
        </AdminCard>
      )}
    </div>
  );
};

export default ManagementCosmetic;
